import { useCallback } from "react";
import { fetchWithAuth } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import { getDateRange } from "@/lib/utils";

// Define the DriversMetrics interface
export interface DriversMetrics {
  active_drivers: number;
  active_worker_types: {
    "full-time": number;
    "part-time": number;
    freelance: number;
  };
  average_road_time_minutes: number;
  drivers_actives_now: number;
  inactive_drivers: number;
  overall_completion_rate_percentage: number;
  overall_delayed_rate_percentage: number;
  total_amount_collected: number;
  total_completed_orders: number;
  total_delayed_orders: number;
  total_drivers: number;
  total_hours_worked: number;
  total_orders: number;
  snapshot_drivers: {
    active_drivers: number;
    inactive_drivers: number;
    active_worker_types: {
      "full-time": number;
      "part-time": number;
      freelance: number;
    };
  };
  trips_per_driver_average: Array<{
    average_trips_per_driver: number;
    date: string;
    total_active_drivers: number;
    total_trips: number;
  }>;
}

// Define filters interface for drivers metrics
export interface DriversMetricsFilters {
  branchId?: string;
  brandId?: string;
  period?: string;
}

export function useDashboardApi() {
  const { getAccessToken } = useAuth();

  const getTokenWithErrorHandling = useCallback(async () => {
    try {
      const token = await getAccessToken();
      return token;
    } catch (error) {
      console.error("Error getting token:", error);
      throw error;
    }
  }, [getAccessToken]);

  // Fetch drivers metrics with filters
  const fetchAllDriversMetrics = async (
    filters?: DriversMetricsFilters,
  ): Promise<DriversMetrics> => {
    const queryParams = new URLSearchParams();

    // Convert period to date range if provided
    const { fromDate, toDate } = getDateRange(
      filters?.period as "day" | "week" | "month",
    );

    // Add filters to query params
    if (filters?.branchId) queryParams.append("branchId", filters.branchId);
    if (filters?.brandId) queryParams.append("brandId", filters.brandId);
    if (fromDate) queryParams.append("fromDate", fromDate);
    if (toDate) queryParams.append("toDate", toDate);

    // Always add empty driverId as shown in the curl
    queryParams.append("driverId", "");

    const url = `/driver/drivers/metrics/all?${queryParams.toString()}`;

    try {
      const token = await getTokenWithErrorHandling();
      const response = await fetchWithAuth(url, token);
      if (!response) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response;
      return data;
    } catch (error) {
      console.error("Error fetching drivers metrics:", error);
      throw error;
    }
  };

  return {
    fetchAllDriversMetrics,
  };
}

export default useDashboardApi;
